import { useCallback, useState } from 'react';

export interface CursorPageInfo {
  hasNextPage: boolean;
  nextCursor: string | null;
}

export interface UseCursorPaginationReturn {
  cursor: string | undefined;
  hasPrevious: boolean;
  currentPage: number;
  goToNext: (pageInfo: CursorPageInfo) => void;
  goToPrevious: () => void;
  reset: () => void;
}

/**
 * Cursor-stack pagination shared by every list hook in the module
 * (`use-ingestions`, `use-configurations`, `use-ingestion-records`): tracks
 * cursor history so the caller can page forward and backward without
 * knowing how cursors are transported over the wire.
 *
 * PDGS exposes no total count, so `currentPage` is simply the 1-based depth
 * of the cursor stack rather than "page N of M".
 *
 * `goToNext` takes the current page's `pageInfo` as a call-time argument
 * rather than as a hook argument. Calling hooks need `cursor` to build the
 * request *before* they have this render's `pageInfo` — it only comes back
 * once that request resolves — so `pageInfo` can't be threaded in until the
 * caller actually invokes `goToNext`.
 *
 * `reset` is a stable reference so calling hooks can put it in a
 * `useEffect` dependency array (e.g. to restart at page 1 when filters
 * change) without retriggering the effect on every render.
 */
export const useCursorPagination = (): UseCursorPaginationReturn => {
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>(
    [undefined],
  );
  const [cursorIndex, setCursorIndex] = useState(0);

  const goToNext = (pageInfo: CursorPageInfo): void => {
    const { hasNextPage, nextCursor } = pageInfo;

    if (!hasNextPage || !nextCursor) {
      return;
    }

    setCursorHistory(previous => [
      ...previous.slice(0, cursorIndex + 1),
      nextCursor,
    ]);
    setCursorIndex(previous => previous + 1);
  };

  const goToPrevious = (): void => {
    if (cursorIndex === 0) {
      return;
    }

    setCursorIndex(previous => previous - 1);
  };

  const reset = useCallback((): void => {
    setCursorHistory([undefined]);
    setCursorIndex(0);
  }, []);

  return {
    cursor: cursorHistory[cursorIndex],
    hasPrevious: cursorIndex > 0,
    currentPage: cursorIndex + 1,
    goToNext,
    goToPrevious,
    reset,
  };
};
